import { __rest } from 'tslib';
import { createNamedContext, hasUtilClass, scrollIntoView, isNil } from '@setel/web-utils';
import * as React from 'react';
import cx from 'classnames';
import { TabPanel, Tabs as Tabs$1, useTabsContext, Tab as Tab$1, TabPanels as TabPanels$1, TabList as TabList$1 } from '@reach/tabs';

const LoaderContext = /*#__PURE__*/createNamedContext('LoaderContext', { isLoading: true });

/**
 * A `<Loader />` component is used to indicate the progress for determinate and indeterminate processes.
 *
 * Indeterminate progress (pending status): `skeleton`, `spinner-blue` or  `spinner-light`.
 *
 * Determinate progress (complete): `checked`.
 *
 */
const Loader$1 = (_a) => {
    var { isLoading = true } = _a, props = __rest(_a, ["isLoading"]);
    return (React.createElement(LoaderContext.Provider, { value: React.useMemo(() => ({ isLoading }), [isLoading]) }, isLoading ? (React.createElement(React.Fragment, null,
        props.type === 'skeleton' && React.createElement(Skeleton, Object.assign({}, props)),
        props.type === 'spinner' && React.createElement(Spinner, Object.assign({}, props)),
        props.type === 'spinner-blue' && React.createElement(Spinner, Object.assign({}, props)),
        props.type === 'spinner-light' && React.createElement(Spinner, Object.assign({}, props)),
        props.type === 'spinner-red' && React.createElement(Spinner, Object.assign({}, props)),
        props.type === 'checked' && React.createElement(Checked, Object.assign({}, props)))) : (props.children)));
};
const Skeleton = ({ className, children }) => (React.createElement("div", { className: cx('animate-pulse', className) }, children));
const Spinner = ({ className, type }) => (React.createElement("svg", { "data-setel-loader-spinner": true, xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 50 50", className: cx(!hasUtilClass(className, 'wu-w-', 'w-') && 'wu-max-w-[4rem] wu-w-full', className) },
    React.createElement("circle", { className: cx('path', type === 'spinner-blue' && 'blue', type === 'spinner-light' && 'light', type === 'spinner-red' && 'red'), cx: "25", cy: "25", r: "20", fill: "none" })));
const Checked = ({ className }) => (React.createElement("svg", { "data-setel-loader-checked": true, xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 154 154", className: cx(!hasUtilClass(className, 'wu-w-', 'w-') && 'wu-max-w-[4rem] wu-w-full', className) },
    React.createElement("g", { fill: "none" },
        React.createElement("circle", { cx: "77", cy: "77", r: "72" }),
        React.createElement("circle", { id: "colored", cx: "77", cy: "77", r: "72" }),
        React.createElement("polyline", { points: "43.5,77.8 63.7,97.9 112.2,49.4" }))));

/**
 * Used as direct child of `<Loader type="skeleton">`.
 *
 * Usage:
 *
 * `<Loader.SkeletonItem>Content to be loaded</Loader.SkeletonItem>`.
 *
 */
const SkeletonItem = ({ children }) => {
    const { isLoading } = React.useContext(LoaderContext);
    return (React.createElement("div", { className: isLoading ? 'wu-bg-[#f1f3f4] wu-rounded-md wu-text-transparent' : '' }, children));
};

const Loader = /*#__PURE__*/Object.assign(Loader$1, {
    SkeletonItem,
});

/**
 * `<Button />` is a wrapper over `<button />` html element.
 *
 * Other than the specified props, it also accepts all props that is accepted by `<button />` element.
 *
 * `ref` will be forwarded to the underlying `<button />` element.
 */
const Button = /*#__PURE__*/React.forwardRef(function Button(_a, forwardedRef) {
    var { type = 'button', variant = 'primary', size = 'medium', minWidth = false, onlyIcon, leftIcon, rightIcon, className, disabled, isLoading, children, render, _spinnerClassName } = _a, props = __rest(_a, ["type", "variant", "size", "minWidth", "onlyIcon", "leftIcon", "rightIcon", "className", "disabled", "isLoading", "children", "render", "_spinnerClassName"]);
    const enabled = !disabled && !isLoading;
    let onlyIconNode = null;
    if (onlyIcon && React.isValidElement(onlyIcon)) {
        onlyIconNode = React.cloneElement(onlyIcon, {
            className: cx(!hasUtilClass(onlyIcon.props.className, 'w-') && 'wu-w-[1.5rem]', !hasUtilClass(onlyIcon.props.className, 'h-') && 'wu-h-[1.5rem]', onlyIcon.props.className),
        });
    }
    let leftIconNode = null;
    if (leftIcon && React.isValidElement(leftIcon)) {
        leftIconNode = React.cloneElement(leftIcon, {
            className: cx(!hasUtilClass(leftIcon.props.className, 'w-') && 'wu-w-[1.5rem]', !hasUtilClass(leftIcon.props.className, 'h-') && 'wu-h-[1.5rem]', 'wu-mr-[0.5rem] wu-flex-shrink-0 wu--my-1', leftIcon.props.className),
        });
    }
    let rightIconNode = null;
    if (rightIcon && React.isValidElement(rightIcon)) {
        rightIconNode = React.cloneElement(rightIcon, {
            className: cx(!hasUtilClass(rightIcon.props.className, 'w-') && 'wu-w-[1.5rem]', !hasUtilClass(rightIcon.props.className, 'h-') && 'wu-h-[1.5rem]', 'wu-ml-[0.5rem] wu-flex-shrink-0 wu--my-1', rightIcon.props.className),
        });
    }
    const merge = (prefix, defaultClass) => !hasUtilClass(className, prefix) && defaultClass;
    const btnClass = cx('wu-relative wu-inline-flex wu-justify-center wu-items-center wu-align-middle wu-px-6 wu-rounded', 'wu-text-sm wu-leading-5 wu-font-bold wu-uppercase wu-tracking-[0.13em] wu-border', isLoading
        ? [
            'wu-text-transparent',
            {
                primary: cx(merge('wu-bg-', 'wu-bg-brand-500'), merge('wu-border-', 'wu-border-brand-500')),
                outline: cx('wu-bg-transparent', merge('wu-border-', 'wu-border-brand-200')),
                error: 'wu-bg-error-500 wu-text-white',
                'error-outline': 'wu-border-error wu-bg-transparent',
            }[variant],
        ]
        : disabled
            ? {
                primary: 'wu-bg-grey wu-text-white wu-border-grey',
                outline: 'wu-text-grey-700 wu-border-grey',
                error: 'wu-bg-gray-200 wu-text-white',
                'error-outline': 'wu-border wu-border-error-100 wu-bg-white wu-text-error-100',
            }[variant]
            : {
                primary: cx('wu-text-white', merge('wu-bg-', 'wu-bg-brand-500'), merge('wu-border-', 'wu-border-brand-500')),
                outline: cx(merge('wu-text-', 'wu-text-brand'), merge('wu-border-', 'wu-border-brand-200')),
                error: 'wu-bg-error-500 wu-text-white hover:wu-bg-error-800',
                'error-outline': 'wu-border wu-border-error wu-bg-white wu-text-error wu-bg-opacity-0 hover:wu-bg-opacity-25 active:wu-bg-opacity-50',
            }[variant], enabled
        ? [
            'wu-cursor-pointer active:wu-ring active:wu-ring-opacity-50',
            merge('active:wu-ring-', 'active:wu-ring-brand-200'),
            {
                primary: cx('hover:wu-text-white', merge('hover:wu-bg-', 'hover:wu-bg-brand-700')),
                outline: cx(merge('hover:wu-text-', 'hover:wu-text-brand'), merge('hover:wu-bg-', 'hover:wu-bg-[#00b0ff1a]')),
                // required for type checking
                error: '',
                'error-outline': '',
            }[variant],
        ]
        : 'wu-cursor-default', classesBySize[size], onlyIcon && classesByIconSize[size], minWidth && classesByMinWidth[minWidth], !hasUtilClass(className, 'focus:') && 'focus-visible-ring', className);
    const content = (React.createElement(React.Fragment, null,
        isLoading && (React.createElement("div", { className: "wu-absolute wu-left-1/2 wu-top-1/2 wu-transform wu--translate-x-1/2 wu--translate-y-1/2" },
            React.createElement(Loader, { type: {
                    ['primary']: 'spinner-light',
                    ['outline']: _spinnerClassName ? 'spinner' : 'spinner-blue',
                    ['error']: 'spinner-light',
                    ['error-outline']: 'spinner-red',
                }[variant], className: cx('wu-w-6', _spinnerClassName) }))),
        onlyIconNode || (React.createElement(React.Fragment, null,
            !isLoading && leftIconNode,
            children,
            !isLoading && rightIconNode))));
    if (render) {
        return render({
            className: btnClass,
            children: content,
            ref: forwardedRef,
        });
    }
    return (React.createElement("button", Object.assign({ type: type, disabled: !enabled, className: btnClass }, props, { ref: forwardedRef }), content));
});
const classesBySize = {
    small: 'wu-h-10',
    medium: 'wu-h-12',
    large: 'wu-h-14',
};
const classesByIconSize = {
    small: 'wu-px-[0.703125rem]',
    medium: 'wu-px-[0.953125rem]',
    large: 'wu-px-[1.203125rem]',
};
const classesByMinWidth = {
    medium: 'wu-min-w-48',
    small: 'wu-min-w-32',
    large: 'wu-min-w-48 sm:wu-min-w-56',
};

const InternalBrandThemeContext = createNamedContext('InternalBrandThemeContext', { brand: 'setel' });
const createBrandedComponent = (brand, Component) => {
    const ctxValue = { brand };
    return (props) => (React.createElement(InternalBrandThemeContext.Provider, { value: ctxValue },
        React.createElement(Component, Object.assign({}, props, { className: cx(props.className, brand === 'petronas' && 'petronas-brand') }))));
};
const useBrand = () => React.useContext(InternalBrandThemeContext);

const TabsImpl = (_a) => {
    var { children, items } = _a, props = __rest(_a, ["children", "items"]);
    return (React.createElement(Tabs$1, Object.assign({}, props),
        items && (React.createElement(React.Fragment, null,
            React.createElement(TabList, null, items.map((item, index) => (React.createElement(Tab, { label: item.label, key: index })))),
            React.createElement(TabPanels, null, items.map((item, index) => (React.createElement(TabPanel, { key: index }, item.content)))))),
        children));
};
const Tab = (_a) => {
    var { label, badge, leftIcon } = _a, props = __rest(_a, ["label", "badge", "leftIcon"]);
    const currentIndex = React.useContext(TabIndexContext);
    const tabListLayout = React.useContext(TabListLayoutContext);
    const { selectedIndex } = useTabsContext();
    const { brand } = useBrand();
    const isActive = currentIndex === selectedIndex;
    const tabRef = React.useRef(null);
    React.useEffect(() => {
        if (isActive && tabRef.current && tabListLayout.tabListRef.current) {
            scrollIntoView(tabRef.current, {
                inline: 'end',
                boundary: tabListLayout.tabListRef.current,
                behavior: 'auto',
                scrollMode: 'if-needed',
            });
        }
    }, [isActive]);
    return (React.createElement(Tab$1, Object.assign({}, props, { className: cx(`wu-flex-shrink-0 wu-inline-flex wu-items-center wu-gap-1 wu-px-2 wu-py-3 wu-text-sm`, {
            petronas: `focus-visible-ring-petronas hover:wu-text-petronas-brand`,
            setel: `focus-visible-ring hover:wu-text-brand-500`,
        }[brand], isActive
            ? {
                setel: `wu-text-brand-500`,
                petronas: `wu-text-petronas-brand`,
            }[brand]
            : 'wu-text-lightgrey', tabListLayout.layout === 'fit' && 'wu-flex-1 wu-w-0 wu-justify-center', props.className), index: currentIndex, ref: tabRef }),
        React.isValidElement(leftIcon)
            ? React.cloneElement(leftIcon, {
                className: cx(`wu-flex-shrink-0 wu-h-5 wu-w-5`, {
                    setel: ``,
                    petronas: `hover:wu-text-petronas-brand`,
                }[brand], isActive
                    ? {
                        setel: `wu-text-brand-500`,
                        petronas: `wu-text-petronas-brand`,
                    }[brand]
                    : 'wu-text-lightgrey', leftIcon.props.className),
            })
            : leftIcon,
        label,
        !isNil(badge) && React.createElement(TabBadge, { isActive: isActive }, badge)));
};
const TabPanels = (props) => React.createElement(TabPanels$1, Object.assign({}, props));
const TabList = (_a) => {
    var { layout = 'auto', children } = _a, props = __rest(_a, ["layout", "children"]);
    const tabListRef = React.useRef(null);
    return (React.createElement("div", { className: "wu-overflow-x-auto web-ui-scrollbar-none wu-border-b wu-border-offwhite", ref: tabListRef },
        React.createElement(TabList$1, Object.assign({}, props, { className: cx('wu-flex wu-gap-4 wu-w-full', props.className), "data-setel-tab-layout": layout }),
            React.createElement(TabListLayoutContext.Provider, { value: React.useMemo(() => ({ layout, tabListRef }), [layout]) }, React.Children.map(children, (child, index) => (React.createElement(TabIndexContext.Provider, { value: index, key: index }, child)))))));
};
const TabBadge = ({ children, isActive, }) => {
    const { brand } = useBrand();
    return (React.createElement("span", { className: cx(`wu-inline-flex wu-items-center wu-px-2.5 wu-py-0.5 wu-text-xs wu-rounded-[10px]`, {
            setel: ``,
            petronas: `hover:wu-bg-petronas-brand-100 hover:wu-text-petronas-brand hover:wu-bg-opacity-20`,
        }[brand], isActive
            ? {
                setel: `wu-text-brand-500 wu-bg-brand-50`,
                petronas: `wu-text-petronas-brand wu-bg-petronas-brand-100 wu-bg-opacity-20`,
            }[brand]
            : 'wu-text-darkgrey wu-bg-gray-100') }, children));
};
const TabIndexContext = createNamedContext('TabIndexContext', undefined);
const TabListLayoutContext = createNamedContext('TabListLayoutContext', { layout: 'auto', tabListRef: { current: null } });
/**
 * Known issue: scroll to active tab not working in iframe
 */
const Tabs = /*#__PURE__*/Object.assign(TabsImpl, {
    Tab,
    TabList,
    Panel: TabPanel,
    Panels: TabPanels,
});

export { Button as B, Loader as L, SkeletonItem as S, Tabs as T, createBrandedComponent as c };
//# sourceMappingURL=tabs-72208780.js.map
